'use client';

import { useState, useEffect } from 'react';

const CONSENT_KEY = 'cookie_consent';

export type ConsentStatus = 'accepted' | 'declined' | null;

export function getConsentStatus(): ConsentStatus {
  if (typeof window === 'undefined') return null;
  const stored = localStorage.getItem(CONSENT_KEY);
  if (stored === 'accepted' || stored === 'declined') return stored;
  return null;
}

interface CookieConsentBannerProps {
  onConsentChange?: (status: ConsentStatus) => void;
}

export default function CookieConsentBanner({ onConsentChange }: CookieConsentBannerProps) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const status = getConsentStatus();
    if (!status) {
      setVisible(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem(CONSENT_KEY, 'accepted');
    setVisible(false);
    onConsentChange?.('accepted');
    // Dispatch custom event so GoogleAnalytics component can react
    window.dispatchEvent(new CustomEvent('cookieConsentUpdate', { detail: 'accepted' }));
  };

  const handleDecline = () => {
    localStorage.setItem(CONSENT_KEY, 'declined');
    setVisible(false);
    onConsentChange?.('declined');
    window.dispatchEvent(new CustomEvent('cookieConsentUpdate', { detail: 'declined' }));
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6"
    >
      <div className="mx-auto max-w-4xl bg-gray-900 border border-gray-700 rounded-xl shadow-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        {/* Icon */}
        <div className="flex-shrink-0 text-2xl select-none" aria-hidden="true">🍪</div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-200 leading-relaxed">
            <span className="font-semibold text-white">We use cookies</span> to analyse site usage and improve your experience.
            Analytics data is only collected with your consent.{' '}
            <a
              href="/support"
              className="underline text-blue-400 hover:text-blue-300 transition-colors"
            >
              Learn more
            </a>
          </p>
        </div>

        {/* Buttons */}
        <div className="flex flex-shrink-0 gap-3 w-full sm:w-auto">
          <button
            onClick={handleDecline}
            className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 border border-gray-600 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500"
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-500 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
}
